import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSearch, faLayerGroup, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useMccCodes } from '../contexts/MccContext';

const Combobox = ({ options, value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = query === ''
    ? options
    : options.filter((option) =>
        option.toLowerCase().includes(query.toLowerCase())
      );

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
             onChange(event.target.value);
             setQuery(event.target.value);
             setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <FontAwesomeIcon icon={faLayerGroup} className="text-gray-400 w-3 h-3" />
        </div>
      </div>
      
      {isOpen && filteredOptions.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-xl py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {filteredOptions.map((option) => (
            <li
              key={option}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-900 dark:text-white"
              onClick={() => {
                onChange(option);
                setQuery('');
                setIsOpen(false);
              }}
            >
              <span className={`block truncate ${value === option ? 'font-bold' : 'font-normal'}`}>
                {option}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MccCodeEditor = () => {
    const { t } = useTranslation();
    const { codes: contextCodes, categories, refreshCodes } = useMccCodes(); 
    const [codes, setCodes] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [editingCategoryId, setEditingCategoryId] = useState(null); // ID of category being edited inline
    const [editingCategoryName, setEditingCategoryName] = useState(""); // Name being edited inline

    // Form state
    const [formData, setFormData] = useState({ code: '', description: '', category: '' });
    const [categoryFormData, setCategoryFormData] = useState({ name: '', id: null });
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCodes();
    }, []);

    const fetchCodes = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/mcc-codes');
            setCodes(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (editingCode) {
                await axios.put(`/mcc-codes/${editingCode._id}`, formData);
            } else {
                await axios.post('/mcc-codes', formData);
            }
            fetchCodes();
            refreshCodes();
            closeModal();
        } catch (error) {
            setError(error.response?.data?.error || "Error saving code");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('confirmDelete', 'Ви впевнені?'))) return;
        try {
            await axios.delete(`/mcc-codes/${id}`);
            fetchCodes();
            refreshCodes();
        } catch (error) {
            console.error(error);
        }
    };

    const openModal = (code = null) => {
        if (code) {
            setEditingCode(code);
            setFormData({ code: code.code, description: code.description, category: code.category });
        } else {
            setEditingCode(null);
            setFormData({ code: '', description: '', category: 'General' });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCode(null);
        setError(null);
    };

    // Category Management
    const openCategoryModal = (cat = null) => {
        if (cat) {
            setCategoryFormData({ name: cat.name, id: cat._id });
        } else {
            setCategoryFormData({ name: '', id: null });
        }
        setIsCategoryModalOpen(true);
    };

    const closeCategoryModal = () => {
        setIsCategoryModalOpen(false);
        setCategoryFormData({ name: '', id: null });
        setEditingCategoryId(null);
        setError(null);
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            await axios.post('/mcc-categories', { name: categoryFormData.name });
            setCategoryFormData({ name: '', id: null }); // Reset add form
            refreshCodes();
        } catch (error) {
            setError(error.response?.data?.error || "Error saving category");
        }
    };
    
    // Inline edit handlers
    const startEditingCategory = (cat) => {
        setEditingCategoryId(cat._id);
        setEditingCategoryName(cat.name);
    };
    
    const cancelEditingCategory = () => {
        setEditingCategoryId(null);
        setEditingCategoryName("");
    };

    const saveEditingCategory = async (id) => {
        try {
            await axios.put(`/mcc-categories/${id}`, { name: editingCategoryName });
            setEditingCategoryId(null);
            setEditingCategoryName("");
            refreshCodes();
        } catch (error) {
             console.error(error);
             // Maybe show specific error for inline edit?
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm(t('confirmDeleteCategory', 'Видалити категорію?'))) return;
        try {
            await axios.delete(`/mcc-categories/${id}`);
            refreshCodes();
        } catch (error) {
            console.error(error);
        }
    };

    // Grouping
    const groupedCodes = codes.reduce((acc, item) => {
        const cat = item.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {});

    const filteredGroups = Object.keys(groupedCodes).reduce((acc, cat) => {
        const filteredItems = groupedCodes[cat].filter(item => 
            item.code.includes(search) || 
            item.description.toLowerCase().includes(search.toLowerCase()) ||
            cat.toLowerCase().includes(search.toLowerCase())
        );
        if (filteredItems.length > 0) {
            acc[cat] = filteredItems;
        }
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            {/* Header Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white dark:bg-dark-surface p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800 dark:text-white">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-primary-500" />
                    {t('mccCodes', 'MCC Коди')}
                </h2>
                
                <div className="flex w-full sm:w-auto gap-2">
                    <div className="relative flex-grow sm:flex-grow-0">
                         <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                         <input 
                            type="text" 
                            placeholder={t('search', 'Пошук...')} 
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                         />
                    </div>
                    <button onClick={() => openCategoryModal()} className="px-4 py-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors font-medium whitespace-nowrap">
                         {t('manageCategories', 'Категорії')}
                    </button>
                    <button onClick={() => openModal()} className="btn-primary rounded-xl px-4 py-2 flex items-center gap-2 whitespace-nowrap">
                        <FontAwesomeIcon icon={faPlus} />
                        <span className="hidden sm:inline">{t('add', 'Додати')}</span>
                    </button>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(filteredGroups).map(([category, items]) => (
                        <div key={category} className="bg-white dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-3 border-b border-gray-100 dark:border-gray-800 font-semibold text-gray-700 dark:text-gray-300">
                                {category}
                            </div>
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {items.map(item => (
                                    <div key={item._id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-sm text-gray-600 dark:text-gray-300 font-bold">
                                                    {item.code}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {item.description}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => openModal(item)}
                                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(item._id)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    {Object.keys(filteredGroups).length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            {t('noCodesFound', 'Коди не знайдені.')}
                        </div>
                    )}
                </div>
            )}

            {/* Modal */}
            {createPortal(
             <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="bg-white dark:bg-dark-surface w-full md:max-w-md rounded-2xl shadow-xl"
                        >
                            <form onSubmit={handleSave}>
                                <div className="p-6 space-y-4">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                        {editingCode ? t('editMccCode', 'Редагувати MCC Код') : t('addMccCode', 'Додати MCC Код')}
                                    </h3>
                                    
                                    {error && (
                                        <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm mb-4 border border-red-100">
                                            {error}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('code', 'Код')}</label>
                                        <input 
                                            type="text" 
                                            required
                                            value={formData.code}
                                            onChange={e => setFormData({...formData, code: e.target.value})}
                                            className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('description', 'Опис')}</label>
                                        <input 
                                            type="text" 
                                            required
                                            value={formData.description}
                                            onChange={e => setFormData({...formData, description: e.target.value})}
                                            className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('category', 'Категорія')}</label>
                                        <Combobox 
                                            options={categories.map(c => c.name)}
                                            value={formData.category}
                                            onChange={(val) => setFormData({...formData, category: val})}
                                            placeholder={t('selectCategory', 'Виберіть категорію')}
                                        />
                                    </div>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2 rounded-b-2xl">
                                    <button 
                                        type="button" 
                                        onClick={closeModal}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-xl transition-colors"
                                    >
                                        {t('cancel', 'Скасувати')}
                                    </button>
                                    <button 
                                        type="submit"
                                        className="btn-primary px-6 py-2 rounded-xl"
                                    >
                                        {t('save', 'Зберегти')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>,
            document.body
           )}
            {/* Category Modal */}
            {createPortal(
             <AnimatePresence>
                {isCategoryModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center sm:p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div 
                            initial={{ opacity: 0, y: "100%" }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="bg-white dark:bg-dark-surface w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[85vh]"
                        >
                             <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-dark-surface z-10">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {t('manageCategories', 'Категорії')}
                                </h3>
                                <button onClick={closeCategoryModal} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                    <FontAwesomeIcon icon={faTimes} size="lg"/>
                                </button>
                             </div>

                             <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
                                {/* Add New Form - Sticky or Top */}
                                <form onSubmit={handleSaveCategory} className="mb-6 flex gap-2">
                                    <input 
                                        type="text" 
                                        placeholder={t('newCategoryName', 'Нова категорія...')}
                                        value={categoryFormData.name}
                                        onChange={e => setCategoryFormData({...categoryFormData, name: e.target.value})}
                                        className="flex-grow px-4 py-3 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-white"
                                        required
                                    />
                                    <button type="submit" className="btn-primary w-12 h-12 flex items-center justify-center rounded-xl shrink-0">
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </form>

                                {/* List */}
                                <div className="space-y-2 pb-6">
                                    {categories.map(cat => (
                                        <div key={cat._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl transition-all">
                                            {editingCategoryId === cat._id ? (
                                                <div className="flex-grow flex items-center gap-2">
                                                    <input 
                                                        autoFocus
                                                        type="text" 
                                                        className="flex-grow px-3 py-1.5 bg-white dark:bg-gray-700 rounded-lg border-2 border-primary-500 focus:outline-none dark:text-white"
                                                        value={editingCategoryName}
                                                        onChange={e => setEditingCategoryName(e.target.value)}
                                                        onKeyDown={e => {
                                                            if(e.key === 'Enter') saveEditingCategory(cat._id);
                                                            if(e.key === 'Escape') cancelEditingCategory();
                                                        }}
                                                    />
                                                    <button onClick={() => saveEditingCategory(cat._id)} className="p-2 text-green-500 hover:bg-green-100 rounded-lg">
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </button>
                                                    <button onClick={cancelEditingCategory} className="p-2 text-gray-400 hover:bg-gray-200 rounded-lg">
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="font-medium text-gray-800 dark:text-gray-200 px-2">{cat.name}</span>
                                                    <div className="flex gap-1">
                                                        <button 
                                                            onClick={() => startEditingCategory(cat)}
                                                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteCategory(cat._id)}
                                                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                             </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>,
            document.body
           )}
        </div>
    );
};

export default MccCodeEditor;
